import { Button } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { FileText, GitCompareArrows, RotateCcw } from 'lucide-react';

export type TemplateAssetPreview = {
  kind: 'image' | 'file';
  src?: string;
  title?: string;
};

export type TemplateAssetPreviewLayout = 'strip' | 'list';

export type TemplateOverrideDetail = {
  label: string;
  value: string;
  imagePreviews?: TemplateAssetPreview[];
  /** Files module uses vertical list; images module uses horizontal strip. */
  assetPreviewLayout?: TemplateAssetPreviewLayout;
};

interface TemplateCustomizationHintProps {
  customized: boolean;
  message: string;
  resetLabel: string;
  templateValueLabel: string;
  onReset: () => void;
  templateValue?: string;
  templateDetails?: TemplateOverrideDetail[];
  className?: string;
}

function formatInlineTemplatePreview(
  templateValue: string | undefined,
  templateDetails: TemplateOverrideDetail[] | undefined,
): string | null {
  if (templateDetails?.length === 1) {
    const row = templateDetails[0];
    if (!row) return null;
    if (row.imagePreviews?.length) {
      return null;
    }
    return row.label ? `${row.label}: ${row.value}` : row.value;
  }
  if (templateDetails && templateDetails.length > 1) {
    return null;
  }
  const trimmed = templateValue?.trim();
  return trimmed || null;
}

function detailRowKey(row: TemplateOverrideDetail, index: number): string {
  return `${row.label}-${row.value}-${index}`;
}

function assetPreviewKey(item: TemplateAssetPreview, index: number): string {
  return `${item.kind}-${item.src ?? ''}-${item.title ?? ''}-${index}`;
}

function TemplateAssetPreviewStrip({
  items,
}: {
  items: TemplateAssetPreview[];
}) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {items.map((item, index) => (
        <figure
          key={assetPreviewKey(item, index)}
          role="listitem"
          className="shrink-0"
        >
          <img
            src={item.src}
            alt={item.title || `Vorlage ${index + 1}`}
            className="h-14 w-14 rounded-md border border-border/60 bg-muted object-cover"
            loading="lazy"
          />
        </figure>
      ))}
    </div>
  );
}

function TemplateFilePreviewList({ items }: { items: TemplateAssetPreview[] }) {
  return (
    <ul className="w-full min-w-0 space-y-1.5">
      {items.map((item, index) => (
        <li
          key={assetPreviewKey(item, index)}
          className="flex min-w-0 items-center gap-2.5 rounded-lg border border-border/60 bg-card px-3 py-2"
        >
          {item.kind === 'image' && item.src ? (
            <img
              src={item.src}
              alt={item.title || `Vorlage ${index + 1}`}
              className="h-9 w-9 shrink-0 rounded border border-border/50 object-cover"
              loading="lazy"
            />
          ) : (
            <FileText
              className="h-5 w-5 shrink-0 text-muted-foreground"
              aria-hidden
            />
          )}
          {item.title ? (
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/85">
              {item.title}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function TemplateAssetPreviewGallery({
  items,
  layout,
}: {
  items: TemplateAssetPreview[];
  layout?: TemplateAssetPreviewLayout;
}) {
  const useFileListLayout =
    layout === 'list' || items.some((item) => item.kind === 'file');
  if (useFileListLayout) {
    return <TemplateFilePreviewList items={items} />;
  }
  return <TemplateAssetPreviewStrip items={items} />;
}

const resetButtonClassName = cn(
  'h-6 shrink-0 gap-1 rounded-md border-border/70 bg-background px-2',
  'text-[11px] font-medium leading-none text-foreground shadow-none',
  'hover:border-amber-300/60 hover:bg-amber-50/80',
  'dark:hover:border-amber-800/50 dark:hover:bg-amber-950/30',
);

export function TemplateCustomizationHint({
  customized,
  message,
  resetLabel,
  templateValueLabel,
  onReset,
  templateValue,
  templateDetails,
  className,
}: TemplateCustomizationHintProps) {
  if (!customized) return null;

  const inlinePreview = formatInlineTemplatePreview(
    templateValue,
    templateDetails,
  );
  const hasAssetPreviews = templateDetails?.some(
    (row) => (row.imagePreviews?.length ?? 0) > 0,
  );
  const useExpandedLayout =
    (templateDetails?.length ?? 0) > 1 || Boolean(hasAssetPreviews);

  const resetButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onReset}
      className={resetButtonClassName}
    >
      <RotateCcw className="h-3 w-3 text-amber-600/85 dark:text-amber-500/85" />
      {resetLabel}
    </Button>
  );

  if (useExpandedLayout && templateDetails) {
    return (
      <div
        role="note"
        className={cn(
          'flex items-start gap-2 text-xs leading-snug text-muted-foreground',
          className,
        )}
      >
        <GitCompareArrows
          className="mt-0.5 h-3 w-3 shrink-0 text-amber-600/75 dark:text-amber-500/80"
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 items-end gap-2">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="font-medium text-foreground/80">{message}</p>
            <p className="opacity-80">{templateValueLabel}:</p>
            <ul className="space-y-1.5">
              {templateDetails.map((row, index) => (
                <li key={detailRowKey(row, index)} className="space-y-1">
                  {row.imagePreviews?.length ? (
                    <>
                      {row.label.trim() ? (
                        <p className="opacity-80">{row.label}</p>
                      ) : null}
                      <TemplateAssetPreviewGallery
                        items={row.imagePreviews}
                        layout={row.assetPreviewLayout}
                      />
                    </>
                  ) : (
                    <p className="flex flex-wrap gap-x-1">
                      <span className="opacity-80">{row.label}:</span>
                      <span className="font-medium text-foreground/75">
                        {row.value}
                      </span>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="shrink-0">{resetButton}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="note"
      className={cn(
        'flex items-center gap-2 text-xs leading-snug text-muted-foreground',
        className,
      )}
    >
      <GitCompareArrows
        className="h-3 w-3 shrink-0 text-amber-600/75 dark:text-amber-500/80"
        aria-hidden
      />
      <p className="min-w-0 flex-1">
        <span className="font-medium text-foreground/80">{message}</span>
        {inlinePreview ? (
          <>
            <span className="mx-1 opacity-50">·</span>
            <span className="opacity-80">{templateValueLabel}: </span>
            <span className="font-medium text-foreground/75">
              {inlinePreview}
            </span>
          </>
        ) : null}
      </p>
      {resetButton}
    </div>
  );
}
